/**
 * EmployerReviewerDossierPage.jsx
 *
 * RC 1.62 Phase C — reviewer-scoped dossier page. Rendered at
 *   /employer/reviews/:reviewId
 *
 * Reads the new `GET /api/employer/v1/reviewer/reviews/{id}/dossier`
 * route (route-layer enforces caller = assigned reviewer).
 *
 * PII-free contract surfaced in the UI:
 *   • candidate identity = short anonymised token (`C-####`)
 *   • NO full name / email / phone shown anywhere
 *   • parsed CV text capped at 12 000 chars by backend
 *   • role definition only exposes title + must-have / nice-to-have
 *     skills + working model + location
 *
 * Reuses existing shadcn primitives + dark theme tokens. No UI
 * redesign — same Card / Badge / Button shapes used elsewhere
 * in the reviewer surface.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FileText, Briefcase, ShieldCheck } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { apiFetch } from '@/lib/api';
import { toast } from 'sonner';
import { DecideDialog, NoteDialog } from '@/pages/EmployerReviewsPage';
import { startReview } from '@/lib/employerReviews';

function genIdempotencyKey() {
  return `reviewer-dossier-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
}

const DECISION_TONE = {
  advance: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30',
  reject:  'bg-rose-500/15 text-rose-700 border-rose-500/30',
  on_hold: 'bg-amber-500/15 text-amber-700 border-amber-500/30',
};

function StatusBadge({ status }) {
  const tone =
    status === 'decided'   ? 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30'
  : status === 'in_review' ? 'bg-blue-500/15 text-blue-700 border-blue-500/30'
  :                          'bg-ndor-surface-alt/60 border-ndor-edge text-ndor-ink-secondary';
  return (
    <Badge variant="outline" className={`uppercase tracking-wider text-[10px] ${tone}`}>
      {(status || 'pending').replace('_', ' ')}
    </Badge>
  );
}

function DecisionBadge({ decision }) {
  if (!decision) return null;
  return (
    <Badge variant="outline"
      className={`uppercase tracking-wider text-[10px] ${DECISION_TONE[decision] || ''}`}>
      {decision}
    </Badge>
  );
}

export default function EmployerReviewerDossierPage() {
  const { reviewId } = useParams();
  const navigate = useNavigate();

  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [decideRow, setDecideRow] = useState(null);
  const [noteRow,   setNoteRow]   = useState(null);
  const [busyStart, setBusyStart] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch(`/api/employer/v1/reviewer/reviews/${reviewId}/dossier`);
      setDossier(data || null);
    } catch (e) {
      setError(e?.data?.detail || e?.message || 'Could not load dossier.');
    } finally {
      setLoading(false);
    }
  }, [reviewId]);

  useEffect(() => { load(); }, [load]);

  const review     = dossier?.review || {};
  const role       = dossier?.role || {};
  const candidate  = dossier?.candidate || {};
  const match      = dossier?.match || null;
  const notes      = dossier?.notes || [];

  const onAfterDecide = useCallback(() => { setDecideRow(null); load(); toast.success('Decision recorded. Your recommendation will be considered by the authorised hiring team before the candidate’s status is changed.'); }, [load]);
  const onAfterNote   = useCallback(() => { setNoteRow(null); load(); toast.success('Note saved.'); }, [load]);
  const onStart = useCallback(async () => {
    if (!review.review_id) return;
    setBusyStart(true);
    try {
      await startReview({ reviewId: review.review_id, idempotencyKey: genIdempotencyKey() });
      toast.success('Review started.');
      load();
    } catch (e) {
      toast.error(e?.message || 'Could not start review.');
    } finally {
      setBusyStart(false);
    }
  }, [review.review_id, load]);

  const isTerminal = review.status === 'decided';

  const formattedCv = useMemo(() => {
    const txt = candidate.parsed_cv_text || '';
    return txt
      .split(/\n+/)
      .map((p) => p.trim())
      .filter(Boolean)
      .slice(0, 80);
  }, [candidate.parsed_cv_text]);

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:py-8 space-y-4" data-testid="reviewer-dossier-page">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate('/employer/reviews')}
          data-testid="reviewer-dossier-back" className="text-ndor-ink-secondary">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to queue
        </Button>
        <div className="text-[11px] text-ndor-ink-muted font-mono">
          Review · V-{(review.review_id || '').slice(0, 8)}
        </div>
      </div>

      {loading && (
        <Card className="border-ndor-edge bg-ndor-surface p-6 text-sm text-ndor-ink-secondary"
              data-testid="reviewer-dossier-loading">
          Loading dossier…
        </Card>
      )}

      {!!error && (
        <Card className="border-rose-500/40 bg-rose-500/5 p-4 text-sm text-rose-700"
              data-testid="reviewer-dossier-error">
          {error}
        </Card>
      )}

      {!loading && !error && dossier && (
        <>
          {/* ── Status + actions row ──────────────────────────── */}
          <Card className="border-ndor-edge bg-ndor-surface p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <StatusBadge status={review.status} />
                  <DecisionBadge decision={review.decision} />
                </div>
                <h1 className="text-lg font-serif-display text-ndor-ink tracking-tight mt-1">
                  Reviewing candidate <span className="font-mono-ui">{candidate.token}</span> for{' '}
                  <span className="text-ndor-ink-secondary">{role.title || 'Untitled role'}</span>
                </h1>
                <p className="text-[11px] text-ndor-ink-muted">
                  Routed {review.routed_at ? new Date(review.routed_at).toLocaleString() : '—'} ·
                  {' '}{review.decided_at ? `Decided ${new Date(review.decided_at).toLocaleString()}` : 'Awaiting decision'}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {review.status === 'pending' && (
                  <Button size="sm" onClick={onStart} disabled={busyStart}
                          data-testid="reviewer-dossier-start-btn">
                    {busyStart ? 'Starting…' : 'Start review'}
                  </Button>
                )}
                {!isTerminal && (
                  <Button size="sm" variant="outline"
                          onClick={() => setNoteRow({ review_id: review.review_id })}
                          data-testid="reviewer-dossier-note-btn">
                    Add note
                  </Button>
                )}
                {!isTerminal && review.status === 'in_review' && (
                  <Button size="sm"
                          onClick={() => setDecideRow({ review_id: review.review_id })}
                          data-testid="reviewer-dossier-decide-btn">
                    Decide
                  </Button>
                )}
              </div>
            </div>
          </Card>

          {/* ── Role + candidate side-by-side ─────────────────── */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Role slice */}
            <Card className="border-ndor-edge bg-ndor-surface p-5 space-y-3" data-testid="reviewer-dossier-role">
              <div className="flex items-center gap-2 text-[11px] text-ndor-ink-muted uppercase tracking-wider">
                <Briefcase className="h-3.5 w-3.5" /> Role definition
              </div>
              <h2 className="text-base font-medium text-ndor-ink">{role.title || '—'}</h2>
              <dl className="text-xs space-y-1.5">
                <div className="flex gap-2">
                  <dt className="text-ndor-ink-muted w-32 shrink-0">Must-have domain</dt>
                  <dd className="text-ndor-ink">{role.must_have_domain || '—'}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-ndor-ink-muted w-32 shrink-0">Working model</dt>
                  <dd className="text-ndor-ink">{role.working_model || '—'}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-ndor-ink-muted w-32 shrink-0">Location</dt>
                  <dd className="text-ndor-ink">{role.location_constraint || '—'}</dd>
                </div>
              </dl>
              {(role.must_have_skills || []).length > 0 && (
                <div>
                  <p className="text-[10px] text-ndor-ink-muted uppercase tracking-wider mb-1">Must-have skills</p>
                  <div className="flex flex-wrap gap-1.5">
                    {role.must_have_skills.slice(0, 24).map((s) => (
                      <Badge key={s} variant="outline"
                             className="border-ndor-edge bg-ndor-surface-alt/60 text-[10px]">{s}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {(role.nice_to_have_skills || []).length > 0 && (
                <div>
                  <p className="text-[10px] text-ndor-ink-muted uppercase tracking-wider mb-1">Nice-to-have</p>
                  <div className="flex flex-wrap gap-1.5">
                    {role.nice_to_have_skills.slice(0, 18).map((s) => (
                      <Badge key={s} variant="outline"
                             className="border-ndor-edge bg-ndor-surface-alt/60 text-[10px] text-ndor-ink-secondary">{s}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            {/* Candidate slice */}
            <Card className="border-ndor-edge bg-ndor-surface p-5 space-y-3" data-testid="reviewer-dossier-candidate">
              <div className="flex items-center gap-2 text-[11px] text-ndor-ink-muted uppercase tracking-wider">
                <ShieldCheck className="h-3.5 w-3.5 text-ndor-gold" /> Candidate (PII-free)
              </div>
              <h2 className="text-base font-mono-ui text-ndor-ink">{candidate.token}</h2>
              <dl className="text-xs space-y-1.5">
                <div className="flex gap-2">
                  <dt className="text-ndor-ink-muted w-32 shrink-0">Primary domain</dt>
                  <dd className="text-ndor-ink">{candidate.primary_domain || '—'}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-ndor-ink-muted w-32 shrink-0">Years experience</dt>
                  <dd className="text-ndor-ink">{candidate.years_experience ?? '—'}</dd>
                </div>
              </dl>
              {(candidate.skills_json || []).length > 0 && (
                <div>
                  <p className="text-[10px] text-ndor-ink-muted uppercase tracking-wider mb-1">Skills (declared)</p>
                  <div className="flex flex-wrap gap-1.5">
                    {candidate.skills_json.slice(0, 30).map((s) => (
                      <Badge key={s} variant="outline"
                             className="border-ndor-edge bg-ndor-surface-alt/60 text-[10px]">{s}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {match && (
                <div className="mt-3 rounded border border-ndor-edge bg-ndor-surface-alt/40 p-3 text-[11px] space-y-1">
                  <p className="font-mono-ui text-ndor-ink-muted">NDOR match metadata</p>
                  <p className="text-ndor-ink">
                    Fit score: <span className="font-mono-ui">{match.fit_score ?? '—'}</span>
                    {match.confidence && <> · Confidence: <span className="font-mono-ui">{match.confidence}</span></>}
                    {match.decision_signal && <> · Signal: <span className="font-mono-ui">{match.decision_signal}</span></>}
                  </p>
                </div>
              )}
            </Card>
          </div>

          {/* ── Parsed CV evidence ───────────────────────────── */}
          <Card className="border-ndor-edge bg-ndor-surface p-5 space-y-3" data-testid="reviewer-dossier-cv">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[11px] text-ndor-ink-muted uppercase tracking-wider">
                <FileText className="h-3.5 w-3.5" /> Parsed CV evidence
              </div>
              {candidate.parsed_cv_truncated_at && (
                <span className="text-[10px] text-ndor-ink-muted">
                  Truncated at {candidate.parsed_cv_truncated_at.toLocaleString()} chars
                </span>
              )}
            </div>
            {formattedCv.length === 0 ? (
              <p className="text-sm text-ndor-ink-muted">No parsed CV text on record.</p>
            ) : (
              <div className="text-sm text-ndor-ink-secondary leading-relaxed space-y-2 max-h-[420px] overflow-y-auto pr-2"
                   data-testid="reviewer-dossier-cv-body">
                {formattedCv.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            )}
          </Card>

          {/* ── Reviewer's own notes ─────────────────────────── */}
          {notes.length > 0 && (
            <Card className="border-ndor-edge bg-ndor-surface p-5 space-y-3" data-testid="reviewer-dossier-notes">
              <div className="flex items-center gap-2 text-[11px] text-ndor-ink-muted uppercase tracking-wider">
                Notes ({notes.length})
              </div>
              <div className="space-y-3">
                {notes.map((n) => (
                  <div key={n.note_id} className="text-xs leading-relaxed border-l-2 border-ndor-gold/40 pl-3">
                    <p className="text-ndor-ink-muted text-[10px] mb-0.5">
                      {n.created_at ? new Date(n.created_at).toLocaleString() : '—'}
                    </p>
                    <p className="text-ndor-ink-secondary whitespace-pre-wrap">{n.body}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Separator className="bg-ndor-edge" />
          {review.rationale_excerpt && (
            <Card className="border-ndor-edge bg-ndor-surface p-5" data-testid="reviewer-dossier-rationale">
              <div className="flex items-center gap-2 text-[11px] text-ndor-ink-muted uppercase tracking-wider mb-2">
                Recorded rationale
              </div>
              <p className="text-sm text-ndor-ink whitespace-pre-wrap">
                {review.rationale_excerpt}
              </p>
            </Card>
          )}
        </>
      )}

      <DecideDialog
        row={decideRow}
        onClose={() => setDecideRow(null)}
        onDone={onAfterDecide}
      />
      <NoteDialog
        row={noteRow}
        onClose={() => setNoteRow(null)}
        onDone={onAfterNote}
      />
    </div>
  );
}
